import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import { THEME_COLOR, THEME_COLOR3 } from "../constants";

const theme = createTheme({
  palette: {
    gray: {
      main: THEME_COLOR3,
    },
    red: {
      main: "#FF0000",
    },
  },
});

const TimePickerSlotProps = {
  actionBar: {
    sx: {
      "& .MuiButton-root": {
        color: "#000",
        fontFamily: "GilroySemiBold",
      },
    },
  },
  digitalClockSectionItem: {
    sx: {
      fontSize: 13,
      fontFamily: "GilroySemiBold",
      "&.Mui-selected": {
        backgroundColor: `${THEME_COLOR}`,
        color: "#000",
        "&:hover": {
          backgroundColor: `${THEME_COLOR}`,
          color: "#000",
        },
      },
    },
  },
  textField: {
    color: "gray",
    variant: "standard",
    InputLabelProps: {
      style: { fontFamily: "GilroyRegular" },
    },
    inputProps: {
      readOnly: true,
      sx: {
        height: 20,
        fontSize: 12,
        fontFamily: "GilroySemiBold",
        padding: "4px 8px",
      },
    },
    sx: {
      svg: { color: "#000", fontSize: 16 },
      "& .MuiButtonBase-root": {
        width: 24,
        height: 24,
      },
      "& .MuiInputBase-root": {
        minHeight: "32px",
        height: "32px",
      },
      "& .MuiInputLabel-root": {
        fontSize: "12px",
        transform: "translate(14px, 8px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -9px) scale(0.75)",
        },
      },
    },
  },
};

const BookingAvailabilityWindows = ({ 
  bookingAvailability = [],
  onBookingAvailabilityChange,
  errors = {}
}) => {
  // State for "All Days" times
  const [allDaysTimes, setAllDaysTimes] = useState({
    openingTime: null,
    closingTime: null
  });

  // Convert array to object for easier manipulation
  const bookingAvailabilityObj = bookingAvailability.reduce((acc, item) => {
    acc[item.dayOfWeek] = item;
    return acc;
  }, {});

  // Simple function to update parent state
  const updateBookingAvailability = (updatedObj) => {
    const updatedArray = Object.values(updatedObj).filter(item => item !== null);
    onBookingAvailabilityChange(updatedArray);
  };

  // Toggle day active/inactive
  const toggleDay = (day) => {
    const isActive = bookingAvailabilityObj[day] !== undefined;
    
    const updatedObj = { ...bookingAvailabilityObj };
    
    if (isActive) {
      delete updatedObj[day];
    } else {
      updatedObj[day] = {
        dayOfWeek: day,
        openingTime: "09:00",
        closingTime: "22:00",
        totalCovers: 0,
        availableCovers: 0
      };
    }
    
    updateBookingAvailability(updatedObj);
  };

  // Update time for a specific day or all days
  const updateTime = (day, timeType, value) => {
    const formatTime = (date) => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    // If it's "All Days", update the all days state
    if (day === 'alldays') {
      setAllDaysTimes(prev => ({
        ...prev,
        [timeType]: value
      }));
      return;
    }

    // Otherwise update specific day
    const updatedObj = { ...bookingAvailabilityObj };
    if (updatedObj[day]) {
      updatedObj[day] = {
        ...updatedObj[day],
        [timeType]: formatTime(value)
      };
      updateBookingAvailability(updatedObj);
    }
  };

  // Apply all days times to ALL days (both active and inactive)
  const applyAllDays = () => {
    const formatTime = (date) => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const updatedObj = {};
    const daysOfWeek = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    
    // Apply to ALL days - activate inactive days and set times
    daysOfWeek.forEach(day => {
      updatedObj[day] = {
        dayOfWeek: day,
        openingTime: formatTime(allDaysTimes.openingTime),
        closingTime: formatTime(allDaysTimes.closingTime),
        totalCovers: 0,
        availableCovers: 0
      };
    });

    updateBookingAvailability(updatedObj);
  };

  // Parse time string to Date object for TimePicker
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return date;
  };

  const renderDay = (day) => {
    const dayData = bookingAvailabilityObj[day.toLowerCase()];
    const isActive = dayData !== undefined;
    const dayName = day.charAt(0).toUpperCase() + day.slice(1);

    return (
      <Grid
        container
        key={day}
        mb={0.5}
        p={1}
        sx={{ 
          border: "1px solid #ddd", 
          borderRadius: "6px",
          backgroundColor: "#fff"
        }}
      >
        {/* Day name and toggle */}
        <Grid container item xs={12} alignItems="center" mb={0.5}>
          <Grid item xs={8}>
            <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "12px" }}>
              {dayName}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <ThemeProvider theme={theme}>
              <Switch
                checked={isActive}
                onChange={() => toggleDay(day.toLowerCase())}
                color="red"
                size="small"
                sx={{
                  "& .MuiSwitch-track": {
                    height: 12,
                    backgroundColor: THEME_COLOR3 + " !important",
                    marginTop: -0.3,
                  },
                  "& .MuiSwitch-thumb": {
                    backgroundColor: "#999",
                    width: 16,
                    height: 16,
                  },
                  "& .Mui-checked .MuiSwitch-thumb": {
                    backgroundColor: "red !important",
                  },
                }}
              />
            </ThemeProvider>
          </Grid>
        </Grid>
        
        {/* Time pickers when active */}
        {isActive && (
          <Grid container spacing={0.5}>
            <Grid item xs={6}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Opening Time"
                    value={parseTime(dayData?.openingTime)}
                    format="hh:mm aa"
                    size="small"
                    onChange={(val) => updateTime(day.toLowerCase(), "openingTime", val)}
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>
            
            <Grid item xs={6}>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Closing Time"
                    value={parseTime(dayData?.closingTime)}
                    format="hh:mm aa"
                    size="small"
                    onChange={(val) => updateTime(day.toLowerCase(), "closingTime", val)}
                    slotProps={TimePickerSlotProps}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  // Render All Days section
  const renderAllDays = () => {
    return (
      <Grid
        container
        mb={1}
        p={1}
        sx={{ 
          border: "1px solid #ddd", 
          borderRadius: "6px",
          backgroundColor: "#f5f5f5"
        }}
      >
        {/* All Days header */}
        <Grid container item xs={12} alignItems="center" mb={0.5}>
          <Grid item xs={12}>
            <Typography sx={{ fontFamily: "GilroySemiBold", fontSize: "12px", fontWeight: "bold" }}>
              All Days
            </Typography>
          </Grid>
        </Grid>
        
        {/* Time pickers for All Days */}
        <Grid container spacing={0.5}>
          <Grid item xs={5}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Opening Time"
                  value={allDaysTimes.openingTime}
                  format="hh:mm aa"
                  size="small"
                  onChange={(val) => updateTime('alldays', "openingTime", val)}
                  slotProps={TimePickerSlotProps}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </Grid>
          
          <Grid item xs={5}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Closing Time"
                  value={allDaysTimes.closingTime}
                  format="hh:mm aa"
                  size="small"
                  onChange={(val) => updateTime('alldays', "closingTime", val)}
                  slotProps={TimePickerSlotProps}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </Grid>
          
          <Grid item xs={2}>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={applyAllDays}
              sx={{
                backgroundColor: THEME_COLOR,
                color: "#000",
                fontFamily: "GilroySemiBold",
                fontSize: "10px",
                height: "32px",
                minWidth: "60px",
                "&:hover": {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.8,
                },
              }}
            >
              Apply All
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontFamily: "GilroySemiBold", mb: 2, fontSize: "16px" }}>
        Booking Availability
      </Typography>
      {renderAllDays()}
      {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(day => 
        renderDay(day)
      )}
    </Box>
  );
};

export default BookingAvailabilityWindows;